<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';

const props = defineProps({
    messages: { type: Array, default: () => [] },
    isGenerating: { type: Boolean, default: false },
});

const emit = defineEmits(['send']);

const messageInput = ref('');
const messagesContainer = ref(null);
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);
const progressWidth = ref(0);
const progressInterval = ref(null);

const funMessages = [
    { icon: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z', text: 'Scanning prospect databases...' },
    { icon: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z', text: 'Searching for ideal matches...' },
    { icon: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21', text: 'Analyzing companies...' },
    { icon: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0', text: 'Filtering by job titles...' },
    { icon: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z', text: 'Mapping locations...' },
    { icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z', text: 'Finding your perfect leads...' },
    { icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z', text: 'Almost there...' },
    { icon: 'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z', text: 'Compiling results...' },
];

const currentFunMessage = computed(() => funMessages[funMessageIndex.value % funMessages.length]);

const startFunMessages = () => {
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;

    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value = (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);

    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopFunMessages = () => {
    if (funMessageInterval.value) {
        clearInterval(funMessageInterval.value);
        funMessageInterval.value = null;
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
    progressWidth.value = 100;
};

watch(() => props.isGenerating, (val) => {
    if (val) startFunMessages();
    else stopFunMessages();
});

const sendMessage = () => {
    const text = messageInput.value.trim();
    if (!text || props.isGenerating) return;
    messageInput.value = '';
    emit('send', text);
};

const handleKeydown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
    }
};

watch(() => props.messages.length, () => {
    nextTick(() => {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    });
});

onUnmounted(() => {
    stopFunMessages();
});

const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat().format(num);
};

const renderMarkdown = (text) => {
    if (!text) return '';
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    // Bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="mf-link">$1</a>');
    // Numbered lists
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li class="ml-4 list-decimal">$2</li>');
    // Bullet lists
    html = html.replace(/^[-•]\s+(.+)$/gm, '<li class="ml-4 list-disc">$1</li>');
    // Paragraphs (double newline)
    html = html.replace(/\n\n/g, '</p><p class="mt-2">');
    // Single newlines
    html = html.replace(/\n/g, '<br>');

    return '<p>' + html + '</p>';
};
</script>

<template>
    <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Messages -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3">
            <div v-if="messages.length === 0 && !isGenerating" class="text-center text-gray-400 text-sm mt-8">
                <div class="flex items-center justify-center w-12 h-12 mx-auto mb-3 rounded-full bg-aiAgentLeadFinder">
                    <ai-agent-lead-finder-avatar class="w-full h-full" />
                </div>
                <p class="mb-1">
                    <mf-translate domainkey="lead-hunter:hugo.emptyState"></mf-translate>
                </p>
                <p class="text-xs">
                    <mf-translate domainkey="lead-hunter:hugo.emptyStateExample"></mf-translate>
                </p>
            </div>

            <div
                v-for="(msg, index) in messages"
                :key="index"
                :class="msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'"
            >
                <!-- User messages -->
                <div
                    v-if="msg.role === 'user'"
                    class="bg-aiAgentLeadFinder text-gray-900 rounded-2xl rounded-br-md px-4 py-2 max-w-[85%] text-sm whitespace-pre-line"
                >
                    {{ msg.content }}
                </div>

                <!-- Assistant messages -->
                <div v-else class="max-w-[90%] space-y-2">
                    <div class="bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md px-4 py-2 text-sm" v-html="renderMarkdown(msg.content)">
                    </div>

                    <!-- Search results -->
                    <div v-if="msg.search_results" class="ml-1">
                        <div class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-aiAgentLeadFinder/40 text-gray-800 rounded-full text-xs font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                            </svg>
                            <mf-translate domainkey="lead-hunter:hugo.foundProspects"></mf-translate>: {{ formatNumber(msg.search_results.total) }}
                        </div>
                    </div>

                    <!-- Filters used -->
                    <div v-if="msg.filters_used" class="ml-1">
                        <p class="text-xs text-gray-500 mb-1">
                            <mf-translate domainkey="lead-hunter:hugo.filtersUsed"></mf-translate>
                        </p>
                        <div class="flex flex-wrap gap-1">
                            <template v-for="(values, filterKey) in msg.filters_used" :key="filterKey">
                                <span
                                    v-for="(val, vIdx) in (Array.isArray(values) ? values : [values])"
                                    :key="`${filterKey}-${vIdx}`"
                                    class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-aiAgentLeadFinder/40 text-gray-800 border border-aiAgentLeadFinder"
                                >
                                    <span class="text-gray-500 mr-1">{{ filterKey.replace(/_/g, ' ') }}:</span>
                                    {{ typeof val === 'object' ? (val.v || JSON.stringify(val)) : val }}
                                </span>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Generating animation -->
            <div v-if="isGenerating" class="flex justify-start generating-appear">
                <div class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-aiAgentLeadFinder bg-gradient-to-br from-aiAgentLeadFinder/80 via-white to-aiAgentLeadFinder/30">
                    <div class="h-1 w-full bg-gray-200 overflow-hidden">
                        <div
                            class="h-full bg-aiAgentLeadFinder transition-all duration-700 ease-out"
                            :style="{ width: progressWidth + '%' }"
                        ></div>
                    </div>
                    <div class="px-5 py-4">
                        <div class="flex items-center gap-3 mb-3">
                            <div class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg bg-aiAgentLeadFinder flex items-center justify-center" :key="funMessageIndex">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" :d="currentFunMessage.icon" />
                                </svg>
                            </div>
                            <span class="text-sm font-medium text-gray-700 generating-text" :key="'t' + funMessageIndex">{{ currentFunMessage.text }}</span>
                        </div>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentLeadFinder/60 skeleton-shimmer" style="width: 65%"></div>
                                <div class="h-2 rounded-full bg-aiAgentLeadFinder/40 skeleton-shimmer" style="width: 25%; animation-delay: 0.3s"></div>
                            </div>
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentLeadFinder/40 skeleton-shimmer" style="width: 40%; animation-delay: 0.15s"></div>
                                <div class="h-2 rounded-full bg-aiAgentLeadFinder/60 skeleton-shimmer" style="width: 50%; animation-delay: 0.45s"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Input area -->
        <div class="border-t border-gray-200 p-3">
            <div class="flex items-end space-x-2">
                <textarea
                    v-model="messageInput"
                    @keydown="handleKeydown"
                    :disabled="isGenerating"
                    :placeholder="_mft('lead-hunter:hugo.placeholder')"
                    rows="2"
                    autofocus
                    class="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-aiAgentLeadFinder focus:border-aiAgentLeadFinder disabled:opacity-50"
                ></textarea>
                <button-base
                    @click="sendMessage"
                    style-type="primary"
                    :disabled="!messageInput.trim() || isGenerating"
                    size="sm"
                    class="flex-shrink-0 !px-2 !py-2"
                >
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                        </svg>
                    </template>
                </button-base>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes skeleton-shimmer {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
}
.skeleton-shimmer {
    animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes generating-appear {
    from { opacity: 0; transform: translateY(8px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}
.generating-appear {
    animation: generating-appear 0.4s ease-out;
}

@keyframes icon-pop {
    0% { transform: scale(0) rotate(-20deg); }
    60% { transform: scale(1.15) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
}
.generating-icon {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slide {
    from { opacity: 0; transform: translateX(-8px); }
    to { opacity: 1; transform: translateX(0); }
}
.generating-text {
    animation: text-slide 0.4s ease-out;
}
</style>
